import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  drawBrandHeader,
  drawMetadataStrip,
  paintBrandedFooters,
  PDF_COLORS,
  PDF_FONT,
  PDF_LAYOUT,
  type ReportBrand,
} from '@/lib/pdf'
import { formatCurrencyPrecise, formatDate } from '@/lib/locale'

export type ScheduleFlavor = 'depreciation' | 'loan' | 'prepaid'

type AnyEntry = {
  period_number: number
  entry_date: string
  amount: number | string
  secondary_amount?: number | string | null
  balance: number | string
  accumulated?: number | string | null
  posted?: boolean
}

type AnySchedule = {
  description?: string | null
  category?: string | null
  initial_amount: number | string
  start_date?: string | null
  duration_periods?: number | null
  params?: Record<string, unknown> | null
}

const TITLES: Record<ScheduleFlavor, string> = {
  depreciation: 'Depreciation Schedule',
  loan: 'Loan Amortization',
  prepaid: 'Prepaid Amortization',
}

const FILE_PREFIX: Record<ScheduleFlavor, string> = {
  depreciation: 'depreciation',
  loan: 'loan_amortization',
  prepaid: 'prepaid_amortization',
}

function fmt(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  return formatCurrencyPrecise(Number.isFinite(n) ? n : 0)
}

function metadataFor(flavor: ScheduleFlavor, schedule: AnySchedule): Array<[string, string]> {
  const rows: Array<[string, string]> = []
  if (schedule.description) rows.push(['Description', schedule.description])
  if (schedule.category) rows.push(['Category', schedule.category])
  rows.push(['Initial amount', fmt(schedule.initial_amount)])
  if (schedule.start_date) rows.push(['Start date', formatDate(schedule.start_date)])
  if (schedule.duration_periods != null) {
    rows.push(['Periods', `${schedule.duration_periods}`])
  }
  if (flavor === 'depreciation') {
    const m = (schedule.params as { method?: string } | null)?.method
    if (m) rows.push(['Method', m])
  }
  if (flavor === 'loan') {
    const p = schedule.params as
      | { lender_name?: string; annual_interest_rate?: number; loan_period_years?: number }
      | null
    if (p?.lender_name) rows.push(['Lender', p.lender_name])
    if (p?.annual_interest_rate != null) {
      rows.push(['Interest rate', `${p.annual_interest_rate}%`])
    }
    if (p?.loan_period_years != null) {
      rows.push(['Term', `${p.loan_period_years} years`])
    }
  }
  return rows
}

function columnsFor(flavor: ScheduleFlavor): string[] {
  if (flavor === 'depreciation') return ['#', 'Period', 'Depreciation', 'Accumulated', 'Book value', 'Posted']
  if (flavor === 'loan') return ['#', 'Date', 'Principal', 'Interest', 'Balance', 'Posted']
  return ['#', 'Period', 'Amortization', 'Accumulated', 'Remaining', 'Posted']
}

function rowFor(flavor: ScheduleFlavor, e: AnyEntry): Array<string | number> {
  const date = formatDate(e.entry_date)
  const posted = e.posted === true ? 'Yes' : 'No'
  if (flavor === 'loan') {
    return [e.period_number, date, fmt(e.amount), fmt(e.secondary_amount ?? 0), fmt(e.balance), posted]
  }
  return [e.period_number, date, fmt(e.amount), fmt(e.accumulated ?? 0), fmt(e.balance), posted]
}

export function downloadSchedulePdf(
  flavor: ScheduleFlavor,
  schedule: AnySchedule,
  entries: AnyEntry[],
  brand: ReportBrand,
): void {
  const doc = new jsPDF({ orientation: 'portrait' })
  const title = TITLES[flavor]
  const subtitle = schedule.description || ''
  let y = drawBrandHeader(doc, brand, title, subtitle)
  y = drawMetadataStrip(doc, y, [
    ...metadataFor(flavor, schedule),
    ['Entries', String(entries.length)],
    ['Generated', new Date().toLocaleDateString()],
  ])

  // Reserve vertical space at the top of every continuation page for
  // the brand header that didDrawPage redraws on each new page. Without
  // this margin, autoTable starts rows at ~40mm (its built-in default),
  // which overlaps the brand header that occupies 14-46mm - the bug
  // users report as "headings overlap text on page 2" of the PDF.
  // headerHeight (30) + topMargin (14) + 6mm buffer = clean separation.
  const continuationTopMargin = PDF_LAYOUT.topMargin + PDF_LAYOUT.headerHeight + 6

  autoTable(doc, {
    startY: y,
    head: [columnsFor(flavor)],
    body: entries.map((e) => rowFor(flavor, e)),
    margin: {
      top: continuationTopMargin,
      left: PDF_LAYOUT.margin,
      right: PDF_LAYOUT.margin,
      bottom: PDF_LAYOUT.bottomMargin,
    },
    styles: {
      fontSize: PDF_FONT.body,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      lineColor: PDF_COLORS.hairline,
      lineWidth: PDF_LAYOUT.thinLine,
      textColor: PDF_COLORS.body,
    },
    headStyles: {
      fillColor: PDF_COLORS.brandPrimary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONT.metaLabel,
    },
    alternateRowStyles: { fillColor: PDF_COLORS.panel },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 30 },
      2: { cellWidth: 'auto', halign: 'right', font: 'courier' },
      3: { cellWidth: 'auto', halign: 'right', font: 'courier' },
      4: { cellWidth: 'auto', halign: 'right', font: 'courier' },
      5: { cellWidth: 18, halign: 'center' },
    },
    // On page 1 the header was already drawn explicitly above (and the
    // metadata strip sits between header and table), so skip the
    // redraw to avoid stomping over the strip. From page 2 onward
    // autoTable continues the table at margin.top - we redraw the
    // header in that reserved band so every page is branded.
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawBrandHeader(doc, brand, title)
      }
    },
    theme: 'grid',
  })

  paintBrandedFooters(doc, brand, title)
  const safeName = (schedule.description || FILE_PREFIX[flavor])
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
  doc.save(`${FILE_PREFIX[flavor]}_${safeName || 'schedule'}_${new Date().toISOString().slice(0, 10)}.pdf`)
}
